import type { ID } from '@octopus-do/octopus-project-core'
import { FactoryComment } from '@store/factory/factory'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { CommentsActions } from '@store/reducers/comments'
import { metaActions } from '@store/reducers/manager/meta'
import { SocketActions } from '@store/socketSaga'
import { getId } from '@store/utils'
import * as ActionTypes from '@storeOld/actions'
import { call, put, select } from 'redux-saga/effects'

import API from '@/api'
import { authSelector } from '@/hooks/useAuth'
import type { AuthState, Comment, Recipient } from '@/types'

interface CreateArg {
  projectId: string
  commentableTitle: string
}

type CreateCb = (arg: CreateArg, id: ID) => Promise<Comment>

export function* create({
  payload,
}: ReturnType<typeof CommentsActions.create>) {
  const projectId: ID = yield select(migrationProjectSelector.getProjectId)
  const users: Recipient[] = yield select(
    migrationProjectSelector.getProjectRecipients
  )

  const auth: AuthState = yield select(authSelector)

  const id = getId()
  const owner = auth.authenticated ? auth.id : null
  // a member's stored name is their real one, whatever the client sent; a
  // guest's is the name they typed for themselves, which arrives in the payload
  const author = owner ? auth.fullName : (payload.author ?? '')

  yield put({
    type: ActionTypes.ADD_COMMENT,
    payload: FactoryComment({
      createdAt: Date.now(),
      updatedAt: Date.now(),
      id,
      ...payload,
      author,
      owner,
    }),
  })

  const commentableTitle: string = yield select(
    migrationProjectSelector.getProjectCommentableTitle(
      payload.commentable,
      payload.commentableType
    )
  )

  // todo: move this logic to the server
  const existentUser = auth.authenticated && users.some((u) => u.id === auth.id)
  if (!existentUser) {
    yield put(metaActions.addRecipient({ ...auth, pending: false }))

    yield put(
      SocketActions.emit({
        eventName: 'sync',
        payload: metaActions.addRecipient({ ...auth, pending: false }),
      })
    )
  }

  const response: Comment = yield call(
    API.Comments.create as CreateCb,
    {
      ...payload,
      commentableTitle,
      projectId,
    },
    id
  )

  const reducerAction = {
    type: ActionTypes.ADD_COMMENT,
    payload: FactoryComment(response),
  }

  yield put({
    type: ActionTypes.UPDATE_PROJECT,
    payload: {
      recipients: users,
    },
  })

  yield put(
    SocketActions.emit({
      eventName: 'comments',
      payload: reducerAction,
    })
  )
}
